import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

/**
 * Configuration for a single URL-synced filter param.
 *
 * `defaultValue` is the value used when the param is absent from the URL; it is
 * also omitted from the URL on write to keep links clean (no noise for unset
 * filters). `parse` is an optional defensive coercion applied to the raw string
 * read from the URL — mirror the Sprint 108 `parseTab` pattern so unknown or
 * malformed values fall back to a sensible default instead of throwing.
 */
export interface UrlFilterParam<T extends string = string> {
  /** Value used when the param is missing; never serialised to the URL. */
  defaultValue: T;
  /**
   * Defensive coercion of the raw URL string to a valid value. Receives the
   * raw param (or `null` when absent) and must return a usable value. Defaults
   * to passing the raw string through (or the default when absent).
   */
  parse?: (raw: string | null) => T;
}

/**
 * Generic config object: a record of param name → its filter config. Keys
 * become the names of the query-string params and the keys of the returned
 * `values` object.
 */
export type UrlFiltersConfig = Record<string, UrlFilterParam>;

/** Resolved values for a given config — one entry per configured param. */
export type UrlFilterValues<C extends UrlFiltersConfig> = {
  [K in keyof C]: C[K] extends UrlFilterParam<infer T> ? T : string;
};

/**
 * Patch passed to `setValues` — any subset of the configured params. Each value
 * may be omitted to leave it unchanged; setting a value equal to its default
 * removes it from the URL.
 */
export type UrlFiltersPatch<C extends UrlFiltersConfig> = Partial<
  UrlFilterValues<C>
>;

export interface UseUrlFiltersResult<C extends UrlFiltersConfig> {
  /** Current resolved values (URL ∪ defaults). */
  values: UrlFilterValues<C>;
  /**
   * Merge a patch of param values into the URL. Values equal to their default
   * are dropped from the query string. By default the current history entry is
   * replaced (no new back-stack entry per keystroke); pass `{ push: true }` to
   * create a navigable history entry instead.
   */
  setValues: (patch: UrlFiltersPatch<C>, options?: { push?: boolean }) => void;
  /** Reset every configured param back to its default (clears them from URL). */
  reset: () => void;
}

function resolveParse<T extends string>(param: UrlFilterParam<T>) {
  return (
    param.parse ??
    ((raw: string | null): T => (raw == null ? param.defaultValue : (raw as T)))
  );
}

/**
 * URL-synced filter state for list/filter pages.
 *
 * Reflects a set of named string params in the query string so the view is
 * reload-safe, history-friendly (back/forward), and shareable/bookmarkable.
 * Designed generically so multiple admin list pages can reuse it (TASK-3698 /
 * TASK-3699): pass a config of `{ paramName: { defaultValue, parse? } }` and
 * read/write through the returned `values` / `setValues`.
 *
 * Behaviour:
 * - Missing or malformed params fall back to their `defaultValue` (via `parse`).
 * - Default values are omitted from the URL — no noise for unset filters.
 * - Writes preserve any unrelated query params already present on the URL.
 */
export function useUrlFilters<C extends UrlFiltersConfig>(
  config: C,
): UseUrlFiltersResult<C> {
  const [searchParams, setSearchParams] = useSearchParams();

  // `searchParams.toString()` is the stable dependency: it changes iff the URL
  // query string changes, so `values` recomputes only when something relevant
  // moved.
  const searchString = searchParams.toString();

  const values = useMemo(() => {
    const params = new URLSearchParams(searchString);
    const result = {} as UrlFilterValues<C>;
    for (const key of Object.keys(config)) {
      const param = config[key];
      const parse = resolveParse(param);
      result[key as keyof C] = parse(
        params.get(key),
      ) as UrlFilterValues<C>[keyof C];
    }
    return result;
  }, [searchString, config]);

  const applyPatchToParams = useCallback(
    (base: URLSearchParams, patch: UrlFiltersPatch<C>) => {
      const next = new URLSearchParams(base);
      for (const key of Object.keys(patch)) {
        const value = patch[key as keyof C];
        if (value === undefined) continue;
        const param = config[key];
        // Omit defaults from the URL to keep links clean.
        if (param && value === param.defaultValue) {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      }
      return next;
    },
    [config],
  );

  const setValues = useCallback(
    (patch: UrlFiltersPatch<C>, options?: { push?: boolean }) => {
      // Each `setValues` uses a SELF-CONTAINED functional updater that applies
      // ONLY its own patch onto whatever `prev` react-router hands it. This is
      // why the wrapper matters:
      //
      // react-router 7's `setSearchParams(fn)` does NOT compose successive
      // functional updaters the way `useState` does — a second updater queued in
      // the same tick receives the CURRENT (pre-first-update) `searchParams`,
      // not the first updater's result. A caller that instead did
      // `setValues({a})` then `setValues({b})` would have the second write
      // rebuild from a `prev` missing `a` and clobber it.
      //
      // The fix is at the call sites (see the page handlers): callers pass ONE
      // combined patch — `setValues({ date_from, page })` — never two calls.
      // Applying only `patch` onto `prev` (rather than re-deriving the whole set)
      // also means that even if two single-key patches were somehow interleaved,
      // each only sets/clears its own key and cannot drop an unrelated one.
      setSearchParams((prev) => applyPatchToParams(prev, patch), {
        replace: !options?.push,
      });
    },
    [applyPatchToParams, setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of Object.keys(config)) {
          next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  }, [config, setSearchParams]);

  return { values, setValues, reset };
}
